import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'aerodeals_session_token';

// Restore token from localStorage on first load and configure axios to send it
// as Authorization header on every request. This works around Safari iOS ITP /
// third-party cookie blocking when the frontend host (www.aerodeals.it) and the
// API host (aviation-exchange-1.emergent.host) are different origins.
const _saved = (() => {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
})();
if (_saved) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_saved}`;
}
// Always send credentials so the cookie path keeps working when same-origin.
axios.defaults.withCredentials = true;

const persistToken = (tok) => {
  try {
    if (tok) {
      localStorage.setItem(TOKEN_KEY, tok);
      axios.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common['Authorization'];
    }
  } catch { /* localStorage unavailable */ }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Capture token from URL fragment (Google OAuth redirect path) and persist
    // it before calling /auth/me — this is the only way to survive Safari ITP
    // when the API host differs from the frontend host.
    try {
      if (typeof window !== 'undefined' && window.location && window.location.hash) {
        const hash = window.location.hash.replace(/^#/, '');
        const params = new URLSearchParams(hash);
        const tok = params.get('auth_token');
        if (tok) {
          persistToken(tok);
          // Strip the token from the URL so it doesn't leak in browser history.
          params.delete('auth_token');
          const cleanedHash = params.toString();
          const newUrl = window.location.pathname + window.location.search + (cleanedHash ? `#${cleanedHash}` : '');
          window.history.replaceState(null, '', newUrl);
        }
      }
    } catch { /* ignore */ }
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data?.token) persistToken(res.data.token);
    setUser(res.data);
    return res.data;
  };

  const register = async (email, password, name, preferred_language = 'it') => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name, preferred_language });
    if (res.data?.token) persistToken(res.data.token);
    setUser(res.data);
    return res.data;
  };

  const loginWithGoogle = (nextPath = '/dashboard') => {
    const next = encodeURIComponent(nextPath);
    window.location.href = `${API}/auth/google/login?next=${next}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch { /* ignore */ }
    persistToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
